import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  almacen,
  precios,
  type CustomerHit,
  type MarginCheck,
  type PriceList,
} from '../api';
import { Boton, Cabecera, ConAyuda, Fallo, Pastilla, Tarjeta, Vacio } from '../componentes';
import { cantidad, soles } from '../formato';

/**
 * Las listas y escalas de precio: «desde 12, S/ 3.20» para todos, y lo que se
 * le cobra a la constructora. Módulo `price_lists`, migración 250.
 *
 * Vive aquí y no en el POS porque es donde está el costo: cada precio se mide
 * contra el costo promedio del almacén, sin IGV, y si queda por debajo del
 * margen mínimo del negocio se avisa o no se deja guardar. Lo que el dueño
 * aprueba aquí se cobra en el mostrador sin pedir firma.
 *
 * **Gana el precio más bajo de los que aplican**, el de carta incluido: lo
 * mismo que calculan el servidor y el POS, con los mismos casos de prueba.
 */
export function Precios() {
  const clientes = useQueryClient();
  const refrescar = () => clientes.invalidateQueries({ queryKey: ['precios'] });

  const listas = useQuery({ queryKey: ['precios', 'listas'], queryFn: precios.lists });
  const reglas = useQuery({ queryKey: ['precios', 'reglas'], queryFn: precios.rules });
  const margen = useQuery({ queryKey: ['precios', 'margen'], queryFn: precios.margin });
  const conLista = useQuery({ queryKey: ['precios', 'clientes'], queryFn: precios.customers });
  const productos = useQuery({ queryKey: ['productos-vendibles'], queryFn: almacen.products });

  const fallo = listas.error ?? reglas.error ?? margen.error ?? conLista.error;

  return (
    <>
      <Cabecera
        titulo="Precios"
        subtitulo="Escalas por cantidad, listas por cliente y el margen mínimo"
        ayuda="Una escala vale para todos desde cierta cantidad; una lista, solo para sus clientes. En el mostrador se cobra lo más barato que le toca a cada uno."
      />
      <div className="cuerpo">
        {fallo ? <Fallo error={fallo} onReintentar={refrescar} /> : null}

        <MargenMinimo
          actual={margen.data ?? null}
          onGuardado={refrescar}
        />

        <Listas listas={listas.data ?? []} onCambio={refrescar} />

        <NuevaRegla
          listas={listas.data ?? []}
          productos={productos.data ?? []}
          onGuardada={refrescar}
        />

        <Tarjeta>
          <h2>Reglas</h2>
          {(reglas.data ?? []).length === 0 ? (
            <Vacio
              titulo="Sin reglas todavía"
              mensaje="Sin reglas, todo se cobra a precio de carta, como siempre."
            />
          ) : (
            <div className="tabla-envoltura">
              <table>
                <thead>
                  <tr>
                    <th>Producto</th>
                    <th>Para</th>
                    <th className="numero">Desde</th>
                    <th className="numero">Carta</th>
                    <th className="numero">Precio</th>
                    <th className="numero">
                      <ConAyuda ayuda="Lo que queda de cada sol cobrado, sin IGV, contra el costo promedio del almacén. Sin costo conocido no se mide.">
                        Margen
                      </ConAyuda>
                    </th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {(reglas.data ?? []).map((regla) => (
                    <tr key={regla.id}>
                      <td>
                        <strong>{regla.productName}</strong>
                      </td>
                      <td className="apagado">{regla.priceListName ?? 'Todos'}</td>
                      <td className="numero mono">{cantidad(regla.minimumQuantity)}</td>
                      <td className="numero apagado">{soles(regla.productPrice)}</td>
                      <td className="numero">{soles(regla.unitPrice)}</td>
                      <td className="numero">
                        {regla.marginPercent === null ? (
                          <span className="apagado">Sin costo</span>
                        ) : (
                          <Pastilla
                            texto={`${regla.marginPercent} %`}
                            tono={regla.belowMinimumMargin ? 'aviso' : 'neutro'}
                          />
                        )}
                      </td>
                      <td>
                        <Boton
                          variante="peligro"
                          onClick={() => void precios.deleteRule(regla.id).then(refrescar)}
                        >
                          Quitar
                        </Boton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Tarjeta>

        <ClientesConLista
          listas={listas.data ?? []}
          clientes={conLista.data ?? []}
          onCambio={refrescar}
        />
      </div>
    </>
  );
}

function MargenMinimo({
  actual,
  onGuardado,
}: {
  actual: { minimumMarginPercent: string | null; policy: 'warn' | 'block' } | null;
  onGuardado: () => void;
}) {
  const [porcentaje, setPorcentaje] = useState<string | null>(null);
  const [politica, setPolitica] = useState<'warn' | 'block' | null>(null);
  const valor = porcentaje ?? actual?.minimumMarginPercent ?? '';
  const regla = politica ?? actual?.policy ?? 'warn';

  const guardar = useMutation({
    mutationFn: () =>
      precios.saveMargin({
        minimumMarginPercent: valor.trim() === '' ? null : valor.trim().replace(',', '.'),
        policy: regla,
      }),
    onSuccess: onGuardado,
  });

  return (
    <Tarjeta>
      <h2>Margen mínimo</h2>
      <p className="nota">
        Se mide al fijar el precio, no en cada venta. Vacío, no hay mínimo.
      </p>
      <div className="formulario__fila">
        <div className="campo">
          <label htmlFor="margen-minimo">Margen mínimo (%)</label>
          <input
            id="margen-minimo"
            inputMode="decimal"
            value={valor}
            onChange={(evento) => setPorcentaje(evento.target.value)}
          />
        </div>
        <div className="campo">
          <label htmlFor="margen-politica">Por debajo</label>
          <select
            id="margen-politica"
            value={regla}
            onChange={(evento) => setPolitica(evento.target.value as 'warn' | 'block')}
          >
            <option value="warn">Avisar y guardar</option>
            <option value="block">No dejar guardar</option>
          </select>
        </div>
        <Boton variante="primario" onClick={() => guardar.mutate()} disabled={guardar.isPending}>
          Guardar
        </Boton>
      </div>
      {guardar.isError ? <Fallo error={guardar.error} /> : null}
    </Tarjeta>
  );
}

function Listas({ listas, onCambio }: { listas: PriceList[]; onCambio: () => void }) {
  const [nombre, setNombre] = useState('');
  const crear = useMutation({
    mutationFn: () => precios.createList(nombre),
    onSuccess: () => {
      setNombre('');
      onCambio();
    },
  });

  const enviar = (evento: FormEvent) => {
    evento.preventDefault();
    if (nombre.trim() !== '') crear.mutate();
  };

  return (
    <Tarjeta>
      <h2>Listas</h2>
      <p className="nota">Mayorista, constructora, convenio: cada cliente va en una.</p>
      <form className="formulario__fila" onSubmit={enviar}>
        <div className="campo">
          <label htmlFor="lista-nueva">Nueva lista</label>
          <input
            id="lista-nueva"
            value={nombre}
            maxLength={80}
            onChange={(evento) => setNombre(evento.target.value)}
          />
        </div>
        <Boton type="submit" variante="primario" disabled={crear.isPending}>
          Crear
        </Boton>
      </form>
      {crear.isError ? <Fallo error={crear.error} /> : null}
      <ul className="lista-simple">
        {listas.map((lista) => (
          <li key={lista.id}>
            <strong>{lista.name}</strong>{' '}
            <span className="apagado">
              {lista.customerCount} clientes · {lista.ruleCount} reglas
            </span>{' '}
            <Boton
              variante="peligro"
              onClick={() => void precios.deleteList(lista.id).then(onCambio)}
            >
              Borrar
            </Boton>
          </li>
        ))}
      </ul>
    </Tarjeta>
  );
}

function NuevaRegla({
  listas,
  productos,
  onGuardada,
}: {
  listas: PriceList[];
  productos: { id: string; name: string; price: string }[];
  onGuardada: () => void;
}) {
  const [productoId, setProductoId] = useState('');
  const [listaId, setListaId] = useState('');
  const [desde, setDesde] = useState('1');
  const [precio, setPrecio] = useState('');
  const [aviso, setAviso] = useState<MarginCheck | null>(null);

  const guardar = useMutation({
    mutationFn: () =>
      precios.saveRule({
        productId: productoId,
        priceListId: listaId === '' ? null : listaId,
        minimumQuantity: desde,
        unitPrice: precio.trim().replace(',', '.'),
      }),
    onSuccess: (resultado) => {
      setAviso(resultado.margin.belowMinimum ? resultado.margin : null);
      setPrecio('');
      onGuardada();
    },
  });

  const enviar = (evento: FormEvent) => {
    evento.preventDefault();
    if (productoId !== '' && precio.trim() !== '') guardar.mutate();
  };

  return (
    <Tarjeta>
      <h2>Nueva regla</h2>
      <form className="formulario__fila" onSubmit={enviar}>
        <div className="campo">
          <label htmlFor="regla-producto">Producto</label>
          <select
            id="regla-producto"
            value={productoId}
            onChange={(evento) => setProductoId(evento.target.value)}
          >
            <option value="">Elige uno</option>
            {productos.map((producto) => (
              <option key={producto.id} value={producto.id}>
                {producto.name} · {soles(producto.price)}
              </option>
            ))}
          </select>
        </div>
        <div className="campo">
          <label htmlFor="regla-lista">Para</label>
          <select id="regla-lista" value={listaId} onChange={(evento) => setListaId(evento.target.value)}>
            <option value="">Todos (escala)</option>
            {listas.map((lista) => (
              <option key={lista.id} value={lista.id}>
                {lista.name}
              </option>
            ))}
          </select>
        </div>
        <div className="campo">
          <label htmlFor="regla-desde">Desde (cantidad)</label>
          <input
            id="regla-desde"
            inputMode="decimal"
            value={desde}
            onChange={(evento) => setDesde(evento.target.value)}
          />
        </div>
        <div className="campo">
          <label htmlFor="regla-precio">Precio con IGV (S/)</label>
          <input
            id="regla-precio"
            inputMode="decimal"
            value={precio}
            onChange={(evento) => setPrecio(evento.target.value)}
          />
        </div>
        <Boton type="submit" variante="primario" disabled={guardar.isPending}>
          Guardar
        </Boton>
      </form>
      {guardar.isError ? <Fallo error={guardar.error} /> : null}
      {aviso === null ? null : (
        <p className="nota">
          Guardada, pero le queda {aviso.marginPercent} % y el mínimo es{' '}
          {aviso.minimumMarginPercent} %.
        </p>
      )}
    </Tarjeta>
  );
}

function ClientesConLista({
  listas,
  clientes,
  onCambio,
}: {
  listas: PriceList[];
  clientes: { id: string; name: string; documentNumber: string | null; priceListId: string }[];
  onCambio: () => void;
}) {
  const [texto, setTexto] = useState('');
  const [encontrados, setEncontrados] = useState<CustomerHit[]>([]);
  const [listaId, setListaId] = useState('');
  const [error, setError] = useState<unknown>(null);

  const buscar = async (evento: FormEvent) => {
    evento.preventDefault();
    setError(null);
    try {
      setEncontrados(await precios.searchCustomers(texto));
    } catch (problema) {
      setError(problema);
    }
  };

  const asignar = async (clienteId: string, lista: string | null) => {
    setError(null);
    try {
      await precios.assignCustomer(clienteId, lista);
      onCambio();
    } catch (problema) {
      setError(problema);
    }
  };

  const nombreDe = (id: string) => listas.find((lista) => lista.id === id)?.name ?? 'Lista';

  return (
    <Tarjeta>
      <h2>Clientes con lista</h2>
      <p className="nota">
        La ficha nace de una venta; aquí solo se le dice a qué precio se le vende.
      </p>
      <form className="formulario__fila" onSubmit={(evento) => void buscar(evento)}>
        <div className="campo">
          <label htmlFor="cliente-buscar">Buscar cliente</label>
          <input id="cliente-buscar" value={texto} onChange={(evento) => setTexto(evento.target.value)} />
        </div>
        <div className="campo">
          <label htmlFor="cliente-lista">Lista</label>
          <select id="cliente-lista" value={listaId} onChange={(evento) => setListaId(evento.target.value)}>
            <option value="">Elige una</option>
            {listas.map((lista) => (
              <option key={lista.id} value={lista.id}>
                {lista.name}
              </option>
            ))}
          </select>
        </div>
        <Boton type="submit">Buscar</Boton>
      </form>
      {error === null ? null : <Fallo error={error} />}
      <ul className="lista-simple">
        {encontrados.map((cliente) => (
          <li key={cliente.id}>
            <strong>{cliente.name}</strong>{' '}
            <span className="apagado">{cliente.documentNumber ?? cliente.phone ?? ''}</span>{' '}
            <Boton
              variante="primario"
              disabled={listaId === ''}
              onClick={() => void asignar(cliente.id, listaId)}
            >
              Poner en la lista
            </Boton>
          </li>
        ))}
      </ul>
      <ul className="lista-simple">
        {clientes.map((cliente) => (
          <li key={cliente.id}>
            <strong>{cliente.name}</strong>{' '}
            <span className="apagado">
              {nombreDe(cliente.priceListId)}
              {cliente.documentNumber === null ? '' : ` · ${cliente.documentNumber}`}
            </span>{' '}
            <Boton variante="peligro" onClick={() => void asignar(cliente.id, null)}>
              Precio de carta
            </Boton>
          </li>
        ))}
      </ul>
    </Tarjeta>
  );
}
